import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";

export default function useRouterStuff() {
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParamsObj = searchParams ? Object.fromEntries(searchParams) : {};

  const getQueryString = (
    kv?: Record<string, string>,
    opts?: {
      ignore?: string[];
    }
  ) => {
    const newParams = new URLSearchParams();
    if (kv) {
      Object.entries(kv).forEach(([k, v]) => newParams.set(k, v));
    }
    if (opts?.ignore) {
      opts.ignore.forEach((k) => newParams.delete(k));
    }
    const queryString = newParams.toString();
    return queryString.length > 0 ? `?${queryString}` : "";
  };

  const debounce = (fn: Function, ms = 300) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
  };

  interface QueryParamsObject {
    set?: Record<string, string | string[]>;
    del?: string | string[];
    replace?: boolean;
    getNewPath?: boolean;
    arrayDelimiter?: string;
    shallow?: boolean;
    clean?: boolean;
  }

  if (!globalThis.queryParamsQueue) globalThis.queryParamsQueue = [];

  const processQueryParams = debounce(() => {
    if (globalThis.queryParamsQueue.length === 0) return;
    let resultParams = new URLSearchParams(Array.from(searchParams.entries()));

    let getNewPath = false;
    let shallow = false;
    let replace = false;

    while (globalThis.queryParamsQueue.length > 0) {
      const params: QueryParamsObject = globalThis.queryParamsQueue.shift();
      if (!params) continue;

      getNewPath = params.getNewPath || false;
      shallow = params.shallow || false;
      replace = params.replace || false;

      if (params.clean) resultParams = new URLSearchParams();

      if (params.set) {
        Object.entries(params.set).forEach(([k, v]) =>
          resultParams.set(
            k,
            Array.isArray(v) ? v.join(params.arrayDelimiter || ",") : v
          )
        );
      }
      if (params.del) {
        if (Array.isArray(params.del)) {
          params.del.forEach((k) => resultParams.delete(k));
        } else {
          resultParams.delete(params.del);
        }
      }
    }

    const queryString = resultParams.toString();
    const newPath = `${pathname}${
      queryString.length > 0 ? `?${queryString}` : ""
    }`;

    if (getNewPath) return newPath;
    if (replace) {
      if (shallow) window.history.replaceState({}, "", newPath);
      else router.replace(newPath);
    } else {
      if (shallow) window.history.pushState({}, "", newPath);
      else router.push(newPath);
    }
  }, 100);

  const queryParams = (params: QueryParamsObject) => {
    globalThis.queryParamsQueue.push(params);
    processQueryParams();
  };

  const shallowRouter = (path: string) => {
    const newPath = `${pathname}${path}`;
    window.history.pushState({}, "", newPath);
  };

  return {
    params,
    pathname,
    router,
    shallowRouter,
    searchParams,
    searchParamsObj,
    queryParams,
    getQueryString,
  };
}
